import { styled } from '@mui/material/styles';
import { ChangeEvent, ReactNode, useState, useEffect } from 'react';
import { QTabContext, QTabList, QTab, QTabPanel, QContainer, QThemeProvider } from '@kw/quokka-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useBeforeUnload } from 'react-router-dom';
import { AiReports } from './AiReports';
import { Integrations } from './Integrations';
import { OrganizationSettings } from './OrganizationSettings';
import { UserManagement } from './UserManagement';
import { isQscoutByokAiEnabled, LaunchDarklyFlags } from '../../launch-darkly';
import { BackgroundProgressBar } from '../BackgroundProgressBar/BackgroundProgressBar';
import { useReloadRestore } from '../Applications/GuardianReport/utils/useReloadRestore';
import { useOrg } from '../Organization/org-provider';
import { orgHasLicense } from '../Organization/org-utils';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { AuthPermissions } from '../../auth/AuthPermissions';

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

export function Settings() {
  const { settingsTabFocus, setSettingsTabFocus, shouldRestore, setShouldRestore } = useReloadRestore();
  const [activeTab, setActiveTab] = useState<string>(settingsTabFocus.root);

  const handleChange = (_event: ChangeEvent<HTMLButtonElement>, tab: string) => {
    setActiveTab(tab);
  };
  const flags: LaunchDarklyFlags['flags'] = useFlags();

  useBeforeUnload(() => {
    setShouldRestore(true);
  });

  useEffect(() => {
    setSettingsTabFocus({ root: activeTab });
    setShouldRestore(false);
  }, [activeTab]);

  useEffect(() => {
    if (!shouldRestore) {
      setSettingsTabFocus({ root: '0' });
    }
  }, []);

  const { orgData } = useOrg();
  // we have two exclusive licenses here: MDM, and PDM
  const isMdmLicense = orgHasLicense(orgData, 'mdm-integration');
  const showAiReports = isQscoutByokAiEnabled(flags) && verifyAuthPermission(AuthPermissions.UPDATE_ORG);

  const tabs: Array<{ label: string; panel: ReactNode }> = [
    ...(isMdmLicense ? [{ label: 'Integrations', panel: <Integrations /> }] : []),
    { label: 'Organization Settings', panel: <OrganizationSettings /> },
    { label: 'Users', panel: <UserManagement /> },
    ...(showAiReports ? [{ label: 'AI Reports', panel: <AiReports /> }] : []),
  ];

  // the restored tab index can point past the tabs available to this org
  useEffect(() => {
    if (Number(activeTab) >= tabs.length) {
      setActiveTab('0');
    }
  }, [activeTab, tabs.length]);

  return (
    <QThemeProvider>
      <QContainer maxWidth={false}>
        <BackgroundProgressBar />
        <QTabContext value={activeTab}>
          <QTabList tabs="true" onChange={handleChange}>
            {tabs.map(({ label }, index) => (
              <StyledQTab key={label} label={label} value={String(index)} {...a11yProps(index)} />
            ))}
          </QTabList>
          {tabs.map(({ label, panel }, index) => (
            <StyledQTabPanel key={label} tabid={String(index)} value={String(index)} index={index}>
              {panel}
            </StyledQTabPanel>
          ))}
        </QTabContext>
      </QContainer>
    </QThemeProvider>
  );
}

const StyledQTab = styled(QTab)`
  text-transform: capitalize;
  font-size: 16px;
  font-weight: 400;
  color: #757575;
  &.Mui-selected {
    color: #000;
    font-weight: 700;
  }
  padding: 20px 40px;
`;

const StyledQTabPanel = styled(QTabPanel)`
  padding: 24px 0;
`;
